import { Injectable, signal, inject } from '@angular/core'
import { DOCUMENT } from '@angular/common'

const KEYS = {
  FONT_SIZE: 'sinsc-font-size',
  HIGH_CONTRAST: 'sinsc-high-contrast',
} as const

const FONT_STEPS = [100, 115, 130] as const
type FontStep = (typeof FONT_STEPS)[number]

@Injectable({ providedIn: 'root' })
export class AccessibilityService {
  private document = inject(DOCUMENT)

  readonly fontSize = signal<FontStep>(this.loadFontSize())
  readonly highContrast = signal(this.loadHighContrast())

  constructor() {
    this.applyFontSize(this.fontSize())
    this.applyHighContrast(this.highContrast())
  }

  increaseFontSize(): void {
    const current = this.fontSize()
    const idx = FONT_STEPS.indexOf(current)
    if (idx < FONT_STEPS.length - 1) {
      this.setFontSize(FONT_STEPS[idx + 1])
    }
  }

  decreaseFontSize(): void {
    const current = this.fontSize()
    const idx = FONT_STEPS.indexOf(current)
    if (idx > 0) {
      this.setFontSize(FONT_STEPS[idx - 1])
    }
  }

  toggleHighContrast(): void {
    this.setHighContrast(!this.highContrast())
  }

  private setFontSize(size: FontStep): void {
    this.fontSize.set(size)
    localStorage.setItem(KEYS.FONT_SIZE, String(size))
    this.applyFontSize(size)
  }

  private setHighContrast(enabled: boolean): void {
    this.highContrast.set(enabled)
    localStorage.setItem(KEYS.HIGH_CONTRAST, String(enabled))
    this.applyHighContrast(enabled)
  }

  private applyFontSize(size: FontStep): void {
    this.document.documentElement.style.fontSize = `${size}%`
  }

  private applyHighContrast(enabled: boolean): void {
    this.document.documentElement.classList.toggle('high-contrast', enabled)
  }

  private loadFontSize(): FontStep {
    const stored = localStorage.getItem(KEYS.FONT_SIZE)
    const parsed = Number(stored) as FontStep
    return FONT_STEPS.includes(parsed) ? parsed : 100
  }

  private loadHighContrast(): boolean {
    return localStorage.getItem(KEYS.HIGH_CONTRAST) === 'true'
  }
}
